/**
 * ProtectedRoute — wraps page content with authentication and RBAC guards.
 *
 * Behaviour:
 * - While auth state is loading (hydrating from localStorage), renders a
 *   neutral "Loading session…" placeholder so pages don't flash.
 * - If the user is not authenticated, redirects to /login/.
 * - If `allowedRoles` is provided, only users whose role exactly matches one
 *   of the listed roles can view the content (strict allow-list). Takes
 *   precedence over `minimumRole`.
 * - If `minimumRole` is provided, users whose role level is >= the required
 *   level can view the content (hierarchical check using ROLE_LEVELS).
 * - If neither prop is provided, any authenticated user can view the content.
 *
 * Usage:
 * ```tsx
 * // Any authenticated user:
 * <ProtectedRoute><MyPage /></ProtectedRoute>
 *
 * // HR and above (hr=3, manager=2, employee=1 — only hr/admin/super_admin pass):
 * <ProtectedRoute minimumRole="hr"><TemplatePage /></ProtectedRoute>
 *
 * // Exact role match (only super_admin):
 * <ProtectedRoute allowedRoles={['super_admin']}><SuperAdminPage /></ProtectedRoute>
 * ```
 */
import { Navigate } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth';
import { type UserRole, isOneOf, hasRole } from '../lib/roles';

type ProtectedRouteProps = {
  children: React.ReactNode;
  /** Require one of these exact roles. Takes precedence over minimumRole. */
  allowedRoles?: UserRole[];
  /** Require at least this role level (inclusive). */
  minimumRole?: UserRole;
};

export function ProtectedRoute({ children, allowedRoles, minimumRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div className="text-sm text-stone-400 p-6">Loading session...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login/" />;
  }

  // Role check
  if (allowedRoles && !isOneOf(user?.role, allowedRoles)) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm font-medium text-stone-600">Access Restricted</p>
        <p className="text-xs text-stone-400 mt-1">
          You don't have permission to view this page.
        </p>
      </div>
    );
  }

  if (minimumRole && !hasRole(user?.role, minimumRole)) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm font-medium text-stone-600">Access Restricted</p>
        <p className="text-xs text-stone-400 mt-1">
          You don't have permission to view this page.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
